import { Box, Collapse } from "@mui/material";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import React, { useMemo, useState } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import ArrowDown from "../../../../../svg/ArrowDown";
import {
  cardioDetails,
  cardioMetric,
  ExerciseLogs,
  expandedSets,
  formatRecovery,
  isCardioExercise,
  isExerciseSkipped,
  loggedSetsFor,
  RangeField,
  ResolvedExercise,
  ResolvedGroup,
  setsRepsSummary,
  splitRecovery,
} from "../../helpers/exerciseSelectors";
import { EXERCISE_GROUP_TYPE, equipmentLabel } from "../../model/workoutEnums";
import { SetV2Model } from "../../model/workoutPlanTypes";
import EquipmentIcon from "../shared/EquipmentIcon";
import RecoveryArrowIcon from "../shared/RecoveryArrowIcon";
import WorkoutTag from "../shared/WorkoutTag";

type Props = {
  exercise: ResolvedExercise;
  group: ResolvedGroup;
  groupIndex: number;
  exerciseIndex: number;
  /** The manual session's logged sets, when the workout was logged. */
  logs: ExerciseLogs | null;
  /** Whether the workout has a merged session (mobile `isCompleted`). */
  isLogged: boolean;
};

const PHOTO_WIDTH = 80;
const PHOTO_HEIGHT = 90;
const EXPAND_MS = 250;

/**
 * One exercise of a V2 workout, after the mobile
 * `WorkoutV2SingleExerciseElement`: photo, name, equipment line, then the
 * elevated sets/reps bar (or the cardio Duration / Zone / metric summary)
 * with a chevron that opens the "Prescribed" set list below the card. Once
 * the workout was logged in the app, the cardio summary shows the logged
 * values and the list carries the mobile "⇄ Prescribed / Logged" toggle — a
 * view switch over data already on screen, it writes nothing; an exercise
 * the member skipped says so instead of its bar. A recovery step between
 * exercises is the dotted-arrow row with its duration.
 */
const ExerciseCard = ({ exercise, group, groupIndex, exerciseIndex, logs, isLogged }: Props) => {
  const theme: Theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [showLogged, setShowLogged] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const groupType = group.type;

  const skipped = isLogged && isExerciseSkipped(logs, groupIndex);
  const loggedSets = useMemo(
    () =>
      isLogged && !skipped
        ? loggedSetsFor(logs, groupIndex, exerciseIndex, groupType, group, exercise.sets.length)
        : null,
    [isLogged, skipped, logs, groupIndex, exerciseIndex, groupType, group, exercise.sets.length]
  );
  const expansion = useMemo(() => {
    const prescribed = expandedSets(exercise, groupType, group.totalRounds);
    // Mobile: a Circuit opens only once logged, with the rounds the member did.
    if (groupType === EXERCISE_GROUP_TYPE.Circuit) {
      return loggedSets ? { heading: `${loggedSets.length} Rounds`, rows: loggedSets } : null;
    }
    return prescribed;
  }, [exercise, groupType, group.totalRounds, loggedSets]);
  const canExpand = !exercise.isRecovery && !skipped && expansion !== null;
  const isCardio = isCardioExercise(exercise);
  // Collapsed cardio summary prefers the logged values once logged (mobile).
  const summaryExercise: ResolvedExercise = loggedSets ? { ...exercise, sets: loggedSets } : exercise;

  if (exercise.isRecovery) {
    return (
      <Box sx={styles.recoveryCard(theme)}>
        <RecoveryArrowIcon fill={theme.colors_ds.NEUTRAL_TEXT_STRONG} />
        <Box sx={styles.recoveryBody}>
          <Box sx={styles.title(theme)}>Rest</Box>
          <RangeFields
            fields={[
              { title: "Duration", value: formatRecovery(exercise.recoverySeconds) || "--" },
            ]}
          />
        </Box>
      </Box>
    );
  }

  const summary = setsRepsSummary(exercise);

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.card(theme)}>
        <Box sx={styles.photoFrame(theme)}>
          {exercise.photoUrl && !photoFailed && (
            <Box
              component="img"
              src={exercise.photoUrl}
              alt=""
              onError={() => setPhotoFailed(true)}
              sx={styles.photo}
            />
          )}
          {exercise.isSwapped && (
            <Box sx={styles.swapTag}>
              <WorkoutTag kind="status" label="Swapped" small />
            </Box>
          )}
        </Box>
        <Box sx={styles.details}>
          <Box sx={styles.title(theme)} title={exercise.title}>
            {exercise.title}
          </Box>
          <Box sx={styles.equipmentRow}>
            <Box sx={styles.equipmentIcon}>
              <EquipmentIcon
                equipment={exercise.equipment}
                fill={theme.colors_ds.NEUTRAL_TEXT_STRONG}
                size={16}
              />
            </Box>
            <Box sx={styles.equipmentText(theme)}>{equipmentLabel(exercise.equipment)}</Box>
          </Box>
          {skipped ? (
            <Box sx={styles.skipped(theme)}>Exercise Skipped</Box>
          ) : (
          <Box
            sx={styles.summaryBar(theme, canExpand)}
            onClick={canExpand ? () => setExpanded((value) => !value) : undefined}
            role={canExpand ? "button" : undefined}
            tabIndex={canExpand ? 0 : undefined}
            aria-expanded={canExpand ? expanded : undefined}
            aria-label={canExpand ? "Expand exercise details" : undefined}
            onKeyDown={
              canExpand
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setExpanded((value) => !value);
                    }
                  }
                : undefined
            }
          >
            {isCardio ? (
              <RangeFields fields={cardioDetails(summaryExercise)} />
            ) : (
              <Box sx={styles.summaryTexts}>
                <Box sx={styles.summaryText(theme)}>
                  {summary.sets} {summary.sets === 1 ? "set" : "sets"}
                </Box>
                <Box sx={styles.summaryText(theme)}>
                  {summary.reps} {summary.reps === 1 ? "rep" : "reps"}
                </Box>
                {summary.weightKg > 0 && (
                  <Box sx={styles.summaryText(theme)}>{summary.weightKg}kg weight</Box>
                )}
              </Box>
            )}
            {canExpand && (
              <Box sx={styles.chevron(expanded)}>
                <ArrowDown width={16} height={16} />
              </Box>
            )}
          </Box>
          )}
        </Box>
      </Box>

      {canExpand && expansion && (
        <Collapse in={expanded} timeout={EXPAND_MS} unmountOnExit>
          <Box sx={styles.expanded}>
            {/* Mobile: once logged, the label becomes a toggle that swaps the
                list between what was prescribed and what the member logged. */}
            {loggedSets ? (
              <Box
                component="button"
                type="button"
                sx={styles.switchButton(theme)}
                onClick={() => setShowLogged((value) => !value)}
                aria-pressed={showLogged}
              >
                <SwapHoriz sx={{ fontSize: 18 }} />
                {showLogged ? "Logged" : "Prescribed"}
              </Box>
            ) : (
              <Box sx={styles.prescribed(theme)}>Prescribed</Box>
            )}
            <SetList
              heading={
                showLogged && loggedSets
                  ? groupType === EXERCISE_GROUP_TYPE.RFT || groupType === EXERCISE_GROUP_TYPE.Circuit
                    ? `${loggedSets.length} Rounds`
                    : `${loggedSets.length} Sets`
                  : expansion.heading
              }
              rows={showLogged && loggedSets ? loggedSets : expansion.rows}
              isCardio={isCardio}
              fallbackSet={exercise.sets[0]}
            />
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ExerciseCard;

type SetListProps = {
  heading: string;
  rows: SetV2Model[];
  isCardio: boolean;
  fallbackSet: SetV2Model;
};

/** The set list: "N Sets" then a row per set, or the single cardio row. */
const SetList = ({ heading, rows, isCardio, fallbackSet }: SetListProps) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.setList}>
      {isCardio ? (
        <CardioRow set={rows[0] ?? fallbackSet} />
      ) : (
        <>
          <Box sx={styles.heading(theme)}>{heading}</Box>
          {rows.map((set, index) => (
            <SetRow key={index} index={index} set={set} />
          ))}
        </>
      )}
    </Box>
  );
};

/** Title-above-value pairs, as the mobile `WorkoutVerticalWithTitleAndValueWidget` row. */
const RangeFields = ({ fields }: { fields: RangeField[] }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.rangeRow}>
      {fields.map((field) => (
        <Box key={field.title} sx={styles.rangeField}>
          <Box sx={styles.rangeTitle(theme)}>{field.title}</Box>
          <Box sx={styles.rangeValue(theme)}>{field.value}</Box>
        </Box>
      ))}
    </Box>
  );
};

/** A small grey label box: the set index, or a unit in the cardio row. */
const Framed = ({ label }: { label: string }) => {
  const theme: Theme = useTheme();
  return <Box sx={styles.framed(theme)}>{label}</Box>;
};

/** Value in medium type with its unit beside it ("3 reps", "40 kg", "2 min"). */
const Cell = ({ value, unit }: { value: string; unit: string }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.cell}>
      <Box sx={styles.cellValue(theme)}>{value}</Box>
      <Box sx={styles.cellUnit(theme)}>{unit}</Box>
    </Box>
  );
};

const Divider = () => {
  const theme: Theme = useTheme();
  return <Box sx={styles.divider(theme)} />;
};

/** One prescribed set: index, reps, weight when set, rest when set. */
const SetRow = ({ index, set }: { index: number; set: SetV2Model }) => {
  const recovery = splitRecovery(set.recovery_time_seconds);
  return (
    <Box sx={styles.setRow}>
      <Framed label={String(index + 1)} />
      <Cell value={set.reps ? String(set.reps) : "--"} unit="reps" />
      {set.weight_kg > 0 && (
        <>
          <Divider />
          <Cell value={String(set.weight_kg)} unit="kg" />
        </>
      )}
      {recovery && (
        <>
          <Divider />
          <Cell value={recovery.value} unit={recovery.unit} />
        </>
      )}
    </Box>
  );
};

/** The single cardio row: minutes, zone, and the metric the set carries. */
const CardioRow = ({ set }: { set: SetV2Model }) => {
  const theme: Theme = useTheme();
  const metric = cardioMetric([set]);
  return (
    <Box sx={styles.setRow}>
      <Framed label="min" />
      <Box sx={styles.cellValue(theme)}>{set.work_time_minutes}</Box>
      <Framed label="zone" />
      <Box sx={styles.cellValue(theme)}>{set.work_zone}</Box>
      {metric && (
        <>
          <Framed label={metric.title.toLowerCase()} />
          <Box sx={styles.cellValue(theme)}>{metric.value}</Box>
        </>
      )}
    </Box>
  );
};

const styles = {
  // The grey sheet the expanded list sits on; the white card overlaps its top.
  container: (theme: Theme) => ({
    borderRadius: "8px",
    backgroundColor: theme.colorTokens.N[40],
  }),
  card: (theme: Theme) => ({
    display: "flex",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  recoveryCard: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  recoveryBody: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  photoFrame: (theme: Theme) => ({
    position: "relative" as "relative",
    flexShrink: 0,
    width: `${PHOTO_WIDTH}px`,
    height: `${PHOTO_HEIGHT}px`,
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
  }),
  photo: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover" as "cover",
  },
  swapTag: {
    position: "absolute" as "absolute",
    top: "4px",
    left: "50%",
    transform: "translateX(-50%)",
  },
  details: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "space-between",
    gap: "6px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  equipmentRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  equipmentIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
  },
  equipmentText: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "20px",
  }),
  // The elevated bar the mobile `ExerciseSetsRepsContainer` draws.
  summaryBar: (theme: Theme, clickable: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
    cursor: clickable ? "pointer" : "default",
    "&:focus-visible": {
      outline: `2px solid ${theme.colorTokens.P[60]}`,
      outlineOffset: "2px",
    },
  }),
  summaryTexts: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  summaryText: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
  }),
  chevron: (expanded: boolean) => ({
    display: "flex",
    flexShrink: 0,
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: `transform ${EXPAND_MS}ms ease`,
  }),
  rangeRow: {
    flex: 1,
    display: "flex",
    gap: "16px",
  },
  rangeField: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  rangeTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  rangeValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  expanded: {
    display: "flex",
    flexDirection: "column" as "column",
    padding: "12px",
  },
  prescribed: (theme: Theme) => ({
    alignSelf: "flex-end",
    padding: "6px 8px",
    marginBottom: "6px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  setList: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  // The mobile toggle: blue bold label with the swap glyph, flush right.
  switchButton: (theme: Theme) => ({
    alignSelf: "flex-end",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    marginBottom: "6px",
    border: "none",
    background: "none",
    cursor: "pointer",
    color: theme.colors_ds.PRIMARY_ICON_MAIN,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
    "&:focus-visible": {
      outline: `2px solid ${theme.colorTokens.P[60]}`,
      outlineOffset: "2px",
      borderRadius: "4px",
    },
  }),
  // The mobile `WorkoutFramedWidget` "Exercise Skipped" in place of the bar.
  skipped: (theme: Theme) => ({
    alignSelf: "flex-start",
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  heading: (theme: Theme) => ({
    marginBottom: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  setRow: {
    display: "flex",
    alignItems: "center",
    height: "32px",
    gap: "8px",
  },
  framed: (theme: Theme) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
    height: "24px",
    padding: "0 8px",
    borderRadius: "4px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  cell: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
    paddingRight: "8px",
  },
  cellValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: "20px",
  }),
  cellUnit: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  divider: (theme: Theme) => ({
    width: "1px",
    height: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  }),
};
